<template>
  <section class="w-full max-w-screen-xl mx-auto px-6 py-10">
    <!-- Item: Produktbeschreibung (title is shown only once, even for multiple items) -->
    <div class="">
      <button
          type="button"
          class="flex w-full items-center justify-between py-7 focus:outline-none select-none"
          @click="isDescriptionOpen = !isDescriptionOpen"
          :aria-expanded="isDescriptionOpen ? 'true' : 'false'"
          aria-controls="description-panel"
      >
        <h3 class="text-2xl leading-tight font-light tracking-wide text-brand-main font-serif">
          {{ $t(`${pageStr}.product_description`) }}
        </h3>
        <span class="text-2xl w-6 mr-10 text-brand-main" aria-hidden="true">
          <svg v-if="!isDescriptionOpen" xmlns="http://www.w3.org/2000/svg" fill="none"
               viewBox="0 0 24 24"
               stroke-width="1.5"
               stroke="currentColor" class="size-10">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
             stroke="currentColor" class="size-10">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/>
        </svg>
        </span>
      </button>
      <div
          id="description-panel"
          v-show="isDescriptionOpen"
          class="pl-10 pb-7 text-sm leading-8 text-black  font-light tracking-widerst"
      >
        {{ description }}
      </div>
    </div>

    <!-- Item: Produktdetails (shown for every item passed in separately) -->
    <div
        v-for="(item, itemIdx) in processedItems"
        :key="`details-${itemIdx}-${item.itemNumber}`"
        class="border-t border-brand-main"
    >
      <button
          type="button"
          class="flex w-full items-center justify-between py-7 focus:outline-none select-none"
          @click="toggleDetails(itemIdx)"
          :aria-expanded="isDetailsOpen[itemIdx] ? 'true' : 'false'"
          :aria-controls="`details-panel-${itemIdx}`"
      >
        <h3 class="text-2xl leading-tight font-light tracking-wide text-brand-main font-serif">
          {{ $t(`${pageStr}.product_details`) }}
          <template v-if="processedItems.length > 1"> {{ itemIdx + 1 }}</template>
        </h3>
        <span class="text-2xl w-6 mr-10 text-brand-main" aria-hidden="true">
                  <svg v-if="!isDetailsOpen[itemIdx]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                       stroke-width="1.5"
                       stroke="currentColor" class="size-10">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
             stroke="currentColor" class="size-10">
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 12h14"/>
        </svg>
        </span>
      </button>

      <div class="pb-6 mx-10">
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-black text-sm">
          <div
              class="flex items-baseline gap-2 whitespace-nowrap"
          >
            <span class="text-brand-main tracking-wide font-bold">{{ $t(`${pageStr}.total_price`) }}:</span>
            <span>{{ $n(item.itemPrice.total, 'currency') }}</span>
          </div>
          <div
              class=" flex items-baseline gap-2 whitespace-nowrap"
          >
            <span class="text-brand-main tracking-wide font-bold">{{ $t(`${pageStr}.item_number`) }}:</span>
            <div class="flex items-center gap-2">
              <button
                  type="button"
                  @click="copyItemNumber(item.itemNumber, itemIdx)"
                  class="text-black hover:opacity-80 focus:outline-none underline decoration-dotted underline-offset-4"
                  :title="$t(`${pageStr}.copy_item_number`)"
              >
                <span class="select-all">{{ item.itemNumber }}</span>
              </button>
              <button
                  type="button"
                  @click="copyItemNumber(item.itemNumber, itemIdx)"
                  :aria-label="$t(`${pageStr}.copy_item_number`)"
                  class="p-1 rounded hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-brand-main"
              >
                <!-- Copy icon -->
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                     stroke="currentColor" class="w-5 h-5">
                  <path stroke-linecap="round" stroke-linejoin="round"
                        d="M8 7.5V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 20 6v7.5A2.25 2.25 0 0 1 17.75 15H16.5m-9 3.75h7.5A2.25 2.25 0 0 0 17.25 16.5v-7.5A2.25 2.25 0 0 0 15 6.75H7.5A2.25 2.25 0 0 0 5.25 9v7.5A2.25 2.25 0 0 0 7.5 18.75Z"/>
                </svg>
              </button>
              <span v-if="copiedIdx === itemIdx" class=" text-brand-main">{{ $t(`${pageStr}.copied`) }}</span>
            </div>
          </div>
          <div
              class="flex items-baseline gap-2 whitespace-nowrap"
          >
            <span class="text-brand-main tracking-wide font-bold">{{ $t(`${pageStr}.certificate`) }}:</span>
            <a v-if="item.itemNumber.includes('R-')" href="/ueber_rubin/nachhaltigkeit#certificate" target="_blank"
               class="text-black underline">
              {{ $t(`${pageStr}.authenticity_manufacturer_certificate`) }}
            </a>
            <a v-else href="/ueber_steinberg/nachhaltigkeit#certificate" target="_blank" class="text-black underline">
              {{ $t(`${pageStr}.authenticity_manufacturer_certificate`) }}
            </a>
          </div>

        </div>
      </div>
      <div :id="`details-panel-${itemIdx}`" v-show="isDetailsOpen[itemIdx]"
           class="pb-6 mx-10 border-t border-brand-main pt-7">
        <!-- product details -->
        <!-- Responsive multi-column grid: first column = root-level ordered, next columns = each part with subs -->
        <div
            class="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-4 text-black text-sm"
        >
          <!-- First column: ordered root-level parts (no subs) including item number and merged width/height -->
          <div>
            <ul role="list" class="space-y-2">
              <li v-for="(row, idx) in item.orderedRootRows" :key="`root-${itemIdx}-${idx}-${row.parameter}`">
                <p v-if="row.title && row.value">
                  <span class="text-brand-main pr-2  tracking-wide"
                        :class="{'font-bold': row.parameter === 'item_type'}"
                  >{{ row.title }}:</span>
                  <span>{{ row.value }}</span>
                </p>
              </li>
              <li v-if="(item.itemPrice?.parts?.item || 0) > 0">
                <span class="text-brand-main pr-2  tracking-wide font-bold">{{ $t(`${pageStr}.item_price`) }}:</span>
                <span>{{ $n(item.itemPrice.parts.item, 'currency') }}</span>
              </li>
            </ul>
          </div>

          <!-- Extra columns: for each part that has subs -->
          <div v-for="(col, cIdx) in item.partsWithSubs" :key="`col-${itemIdx}-${cIdx}-${col.parameter || cIdx}`">
            <div class="mb-2">
              <p v-if="col.title">
                <span class="text-brand-main pr-2  tracking-wide font-bold">{{ col.title }}:</span>
                <span v-if="col.value">{{ col.value }}</span>
              </p>
            </div>
            <div class="ml-2">
              <description-parts-sub :sub-list="col.sub"/>
              <div v-if="col.parameter != 'discs'" class="flex items-baseline gap-2 whitespace-nowrap mb-2">
                <p>
                  <span class="text-brand-main pr-2  tracking-wide">{{ $t(`${pageStr}.origin`) }}:</span>
                  <a href="/diamanten/5cs#confidence" target="_blank"
                     class="text-black underline">{{ $t(`${pageStr}.conflict_free_diamond`) }}</a>
                </p>
              </div>

              <div
                  v-if="(item.itemPrice?.parts[col.parameter] || item.itemPrice?.parts[''] || 0) > 0"
                  class="flex items-baseline gap-2 whitespace-nowrap"
                  :style="{
                gridColumn: (1 + item.partsWithSubs.length) > 1
                  ? '2 / -1'
                  : '1 / -1'
              }"
              >
                <span class="text-brand-main  tracking-wide font-bold">{{
                    $t(`${pageStr}.price_of`, {title: col.title})
                  }}</span>
                <span v-if="item.itemPrice.parts[col.parameter]">{{
                    $n(item.itemPrice.parts[col.parameter], 'currency')
                  }}</span>
                <span v-else-if="item.itemPrice.parts['']">{{ $n(item.itemPrice.parts[''], 'currency') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Bottom border to mirror screenshot spacing -->
    <div class="border-t border-brand-main"/>
  </section>
</template>
<script setup lang="ts">
import {computed, defineProps, ref} from "vue";
import DescriptionPartsSub from "@/components/description/descriptionPartsSub.vue";
import {useI18n} from "vue-i18n";

const {t} = useI18n();

interface DescriptionItem {
  itemPrice: {
    total: number;
    parts: {
      item: number,
      stones: number
    }
  },
  itemNumber: string;
  description: string;
  partsList: any[]
}

const props = defineProps<{
  items: DescriptionItem[]
}>();

const pageStr = "components.description.descriptionParts"

// UI state (matches screenshot: description open, details closed)
const isDescriptionOpen = ref(true);
const isDetailsOpen = ref<boolean[]>([]);

const toggleDetails = (idx: number) => {
  isDetailsOpen.value[idx] = !isDetailsOpen.value[idx];
};

// Copy to clipboard state
const copiedIdx = ref<number | null>(null);

const copyItemNumber = async (itemNumber: string, idx: number) => {
  const text = String(itemNumber ?? '');
  if (!text) return;
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
    } else {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    copiedIdx.value = idx;
    window.setTimeout(() => {
      if (copiedIdx.value === idx) copiedIdx.value = null;
    }, 1500);
  } catch {
    // Ignore errors silently, do not break UI
  }
};

// Title/description is only shown once, taken from the first item
const description = computed(() => props.items?.[0]?.description ?? '');

// Build the ordered root-level rows and the parts-with-subs columns for a single item
const buildItemView = (item: DescriptionItem) => {
  const partsList = item.partsList || [];

  const partsWithSubs = partsList.filter((p: any) => Array.isArray(p?.sub) && p.sub.length > 0);
  const partsWithoutSubs = partsList.filter((p: any) => !Array.isArray(p?.sub) || p.sub.length === 0);

  // Order by `parameter` key; merge width+height on one line
  const order: string[] = [
    'item_type',
    'set_id',
    'metal',
    'color',
    'width_height', // special synthetic key for merged display
    'size',
    'length',
  ];

  const rows: { parameter: string; title: string; value: string }[] = [];

  const itemsByParam = new Map<string, any>();
  for (const p of partsWithoutSubs) {
    if (p && typeof p === 'object' && p.parameter) {
      itemsByParam.set(p.parameter, p);
    }
  }

  // Helper to safely get title/value
  const makeRow = (p: any, fallbackParam: string) => ({
    parameter: p?.parameter ?? fallbackParam,
    title: p?.title ?? '',
    value: p?.value ?? '',
  });

  // Precompute width/height
  const width = itemsByParam.get('width');
  const height = itemsByParam.get('height');

  // Build rows in specified order
  for (const key of order) {
    if (key === 'width_height') {
      if ((width?.value ?? '') !== '' || (height?.value ?? '') !== '') {
        const wTitle = width?.title || t(`${pageStr}.width`);
        const hTitle = height?.title || t(`${pageStr}.height`);
        const wVal = width?.value ?? '';
        const hVal = height?.value ?? '';
        const label = `${wTitle} × ${hTitle}`;
        const value = [wVal, hVal].filter(Boolean).join(' × ');
        rows.push({parameter: 'width_height', title: label, value});
      }
      continue;
    }
    const found = itemsByParam.get(key);
    if (found && (found.value ?? '') !== '') {
      rows.push(makeRow(found, key));
      itemsByParam.delete(key);
    }
  }

  // Append all remaining root-level items not in the order list (excluding width/height already handled)
  for (const [param, itm] of itemsByParam.entries()) {
    if (param === 'width' || param === 'height') continue; // already represented
    if ((itm?.value ?? '') === '') continue;
    rows.push(makeRow(itm, param));
  }

  return {
    itemPrice: item.itemPrice,
    itemNumber: item.itemNumber,
    partsWithSubs,
    orderedRootRows: rows,
  };
};

// Per-item processed view: price, item number, ordered rows and parts-with-subs columns
const processedItems = computed(() => (props.items || []).map(buildItemView));
</script>
